'use client'

import React, { useEffect, useState, useCallback } from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import CouponCallout from '@/src/components/CouponCallout'
import { getCoupons } from '@/src/api/services/visaService'
import { Coupon } from '@/src/types/visa/coupon'
import {
    Button,
    Card,
    Container,
    Spacer,
    Stepper,
    Typography,
    Checkbox,
    Divider
} from '@ct-design/lib'
import { ValidTick } from '@ct-design/assets/svg'
import VisaDetailsStep from './visaDetailsStep'
import TravellerInfoStep from '@/src/container/desktop/visaPageContainer/visaDetails/travellerInfo'
import { DESKTOP } from '@/src/constants'
import { stepTitlesDesktop } from '@/src/constants/visa'
import useVisaFormValidation from '@/src/hooks/useVisaFormValidation'
import useVisaHelperUtils from '@/src/hooks/useVisaHelperUtils'
import {
    createItinerary,
    updateItinerary
} from '@/src/api/services/visaService'
import {
    setItineraryData,
    setSelectedTravellerIndex,
    setTravellerError
} from '@/src/store/slices/visaSlice/itineraryInfoSlice'
import { fetchVisaRequirements } from '@/src/store/slices/visaSlice/visaRequirementsSlice'
import { getTravelDetails } from '@/src/store/slices/visaSlice/travelDetailsSlice'
import { getVisaErrorFields } from '@/src/store/slices/visaSlice/visaErrorSlice'
import { fetchUserProfile } from '@/src/store/slices/visaSlice/userProfileSlice'
import {
    validateTravellerInItinerary,
    getUpdateItineraryPayload
} from '@/src/utils/visa/travellers'
import { showNotification } from '@/src/store/slices/notificationSlice'
import { VISA_ERRORS, ERROR_MESSAGES } from '@/src/constants/error'
import { isLoggedIn, isEmpty, redirectToVisaSignIn } from '@/src/utils/helper'
import VisaBookingsList from '@/src/components/common/visaBookingsList'
import RetriveApplicationCallout from '@/src/components/common/retriveApplicationCallout'
import PageTracker from '@/src/components/common/newRelic/PageTracker'
import TermsAndConditionsModal from '@/src/components/common/termsAndConditionsModal'
import useServiceRule from '@/src/hooks/useServiceRule'
import Image from 'next/image'
import MusafirLogo from '@/src/assets/Musafir.png'
import useVisaGtmEvent from '@/src/hooks/analytics/gtm/useVisaGtmEvent'

interface SectionState {
    completed: boolean
    expanded: boolean
    loading: boolean
    error: string | null
}

const NewVisaUnifiedFlow = ({ initialStep }: { initialStep: number }) => {
    const router = useRouter()
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const dispatch = useAppDispatch()
    const signedIn = isLoggedIn()

    const stepTitles = stepTitlesDesktop

    const [currentStep, setCurrentStep] = useState(initialStep)
    const [coupons, setCoupons] = useState<Coupon[]>([])
    const [isCouponsLoading, setIsCouponsLoading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)

    // Get serviceRule from Redux
    const { serviceRule, isLoading: isServiceRuleLoading } =
        useServiceRule(DESKTOP)

    const [sections, setSections] = useState<{
        visaDetails: SectionState
        traveller: SectionState
    }>({
        visaDetails: {
            completed: false,
            expanded: true,
            loading: false,
            error: null
        },
        traveller: {
            completed: false,
            expanded: false,
            loading: false,
            error: null
        }
    })

    const travelDetailsData = useAppSelector(getTravelDetails)
    const visaRequirements = useAppSelector((state) => state.visaRequirements)
    const itineraryInfo = useAppSelector((state) => state.itineraryInfo)

    const { validateTravelDetails, validateVisaRequirements } =
        useVisaFormValidation()
    const { getCreateItineraryPayload } = useVisaHelperUtils()
    const validationErrors = useAppSelector(getVisaErrorFields)

    // GTM tracking hooks
    const {
        trackVisaHomePageView,
        trackVisaDetailsContinueClick,
        trackVisaReviewButtonClick
    } = useVisaGtmEvent()

    // Track page view on mount
    useEffect(() => {
        trackVisaHomePageView()
    }, [trackVisaHomePageView])

    // Fetch coupons on mount
    useEffect(() => {
        const fetchCoupons = async () => {
            try {
                setIsCouponsLoading(true)
                const response = await getCoupons(DESKTOP)
                if (response?.data?.coupons) {
                    setCoupons(response.data.coupons)
                }
            } catch (error) {
                console.error('Failed to fetch coupons:', error)
            } finally {
                setIsCouponsLoading(false)
            }
        }

        fetchCoupons()
    }, [])

    const handleVisaDetailsUpdate = useCallback(async () => {
        // Validate both travel details and visa requirements
        const { nationality, destination } = travelDetailsData || {}

        if (!validateTravelDetails(nationality, destination)) {
            return
        }

        if (!validateVisaRequirements()) {
            return
        }

        // Set loading state before API call
        setSections((prev) => ({
            ...prev,
            visaDetails: { ...prev.visaDetails, loading: true }
        }))

        try {
            const payload = getCreateItineraryPayload()
            if (isEmpty(payload)) {
                throw new Error('Invalid payload')
            }

            const { status, data } =
                (await createItinerary(payload, DESKTOP)) || {}

            if (status === 200) {
                // Track GTM event only after successful API call
                trackVisaDetailsContinueClick({
                    nationality: (travelDetailsData?.nationality?.value ||
                        travelDetailsData?.nationality) as string | undefined,
                    destination: (travelDetailsData?.destination?.value ||
                        travelDetailsData?.destination) as string | undefined,
                    visaType:
                        visaRequirements?.visaType?.selectedOption?.visaType,
                    processingSpeed: visaRequirements?.processingSpeed?.type,
                    dateOfVisa: visaRequirements?.dateOfArrival || undefined,
                    adults: visaRequirements?.paxDetails?.adults,
                    children: visaRequirements?.paxDetails?.children
                })

                dispatch(setItineraryData({ ...data }))
                setSections((prev) => ({
                    ...prev,
                    visaDetails: {
                        ...prev.visaDetails,
                        completed: true,
                        expanded: false,
                        loading: false
                    },
                    traveller: {
                        ...prev.traveller,
                        expanded: true
                    }
                }))
            } else {
                throw new Error('Failed to create itinerary')
            }
        } catch (error) {
            setSections((prev) => ({
                ...prev,
                visaDetails: {
                    ...prev.visaDetails,
                    loading: false,
                    error: 'Failed to create itinerary'
                }
            }))
            dispatch(
                showNotification({
                    message: ERROR_MESSAGES.GENERIC_ERROR,
                    type: 'ERROR' as any
                })
            )
        }
    }, [
        travelDetailsData,
        visaRequirements,
        validateTravelDetails,
        validateVisaRequirements,
        getCreateItineraryPayload,
        trackVisaDetailsContinueClick,
        dispatch
    ])

    const handleTravellerInfoUpdate = useCallback(async () => {
        // Validate all travellers before proceeding
        const { customerDetails, searchCriteria } = itineraryInfo?.data || {}
        const {
            travellerDetails = [],
            isdCode,
            phoneNumber,
            customerEmail
        } = customerDetails || {}
        const { travellerValidations } = itineraryInfo?.metaData || {}

        // Validate all travellers
        for (let i = 0; i < travellerDetails.length; i++) {
            const contactDetails = { isdCode, phoneNumber, customerEmail }
            const { errorFields, isTravellerValid } =
                validateTravellerInItinerary(
                    travellerDetails[i],
                    contactDetails,
                    travellerValidations,
                    searchCriteria,
                    travellerDetails,
                    i
                )
            if (!isTravellerValid) {
                dispatch(setSelectedTravellerIndex(i))
                dispatch(
                    setTravellerError({
                        index: i,
                        errorFields
                    })
                )
                setSections((prev) => ({
                    ...prev,
                    traveller: {
                        ...prev.traveller,
                        error: `Please complete all required fields for Traveller ${i + 1}`
                    }
                }))
                return
            }
        }

        setSections((prev) => ({
            ...prev,
            traveller: { ...prev.traveller, loading: true, error: null }
        }))

        try {
            // Update itinerary with traveller details
            if (itineraryInfo?.data?.itineraryId) {
                const payload = getUpdateItineraryPayload(
                    customerDetails,
                    itineraryInfo.data.itineraryId
                )

                const response = await updateItinerary(
                    payload,
                    itineraryInfo.data.itineraryId,
                    DESKTOP
                )

                const { status, data } = response || {}
                const { itineraryStatus } = data || {}

                if (status === 200 && itineraryStatus === 'UPDATED') {
                    // Track GTM event only after successful validation and API call
                    const leadTraveller =
                        travellerDetails.find((t) => t.isPrimary) || travellerDetails[0]
                    trackVisaReviewButtonClick(leadTraveller)

                    // Navigate to step 4 for review
                    router.push(
                        `/visa/itinerary/${itineraryInfo.data.itineraryId}/info?step=4`
                    )
                    setSections((prev) => ({
                        ...prev,
                        traveller: {
                            ...prev.traveller,
                            completed: true,
                            loading: false
                        }
                    }))
                } else {
                    throw new Error('Failed to update traveller details')
                }
            } else {
                throw new Error('No itinerary ID found')
            }
        } catch (error) {
            setSections((prev) => ({
                ...prev,
                traveller: {
                    ...prev.traveller,
                    loading: false,
                    error: 'Failed to proceed to review'
                }
            }))
        }
    }, [itineraryInfo, trackVisaReviewButtonClick, router, dispatch])

    const handleSectionToggle = (section: keyof typeof sections) => {
        setSections((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                expanded: !prev[section].expanded
            }
        }))
    }

    const allSectionsCompleted = Object.values(sections).every(
        (s) => s.completed
    )

    const handleLoginClick = () => {
        redirectToVisaSignIn()
    }

    // Fetch user profile on page load
    useEffect(() => {
        dispatch(fetchUserProfile(DESKTOP))
    }, [dispatch])

    // Pre-fill logic is now handled in useTravellerInfo hook

    useEffect(() => {
        if (allSectionsCompleted && itineraryInfo?.data?.itineraryId) {
            router.push(
                `/visa/itinerary/${itineraryInfo.data.itineraryId}/info?step=4`
            )
        }
    }, [allSectionsCompleted, itineraryInfo, router])

    return (
        <>
            <Container className='container p-relative z-0'>
                <Card className='px-8 pt-6 pb-8'>
                    <Stepper
                        steps={stepTitles}
                        currentStep={currentStep}
                        onStepClick={(step) => setCurrentStep(step)}
                    />
                    <Spacer className='mb-4' />

                    <Container className='p-relative h-8 mb-5'>
                        <Container className='p-relative' id='step-title'>
                            <Typography variant='HM1'>
                                {stepTitles[currentStep - 1]}
                            </Typography>
                        </Container>
                    </Container>

                    {/* Coupon Callouts - Show only for Step 1 */}
                    {currentStep === 1 && (
                        <CouponCallout
                            deviceType={DESKTOP}
                            coupons={coupons}
                            isLoading={isCouponsLoading}
                        />
                    )}

                    <Spacer className='mb-4' />

                    <Container
                        id='unified-form-container'
                        className='p-relative'
                    >
                        {/* Combined Visa Details Section */}
                        <Card
                            className={`mb-4 p-4 ${sections.visaDetails.expanded ? 'border-primary' : 'border-default'}`}
                        >
                            <Container
                                className='flex flex-between items-center mb-4'
                                onClick={() =>
                                    !sections.visaDetails.loading &&
                                    handleSectionToggle('visaDetails')
                                }
                                style={{ cursor: 'pointer' }}
                            >
                                <Container
                                    className='flex items-center'
                                    style={{ alignItems: 'center' }}
                                >
                                    <Typography variant='HM2'>
                                        Visa Details
                                    </Typography>
                                    {sections.visaDetails.completed && (
                                        <div
                                            style={{
                                                marginLeft: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                lineHeight: 1
                                            }}
                                        >
                                            <ValidTick
                                                color='#00B28A'
                                                width='16'
                                                height='16'
                                            />
                                        </div>
                                    )}
                                </Container>
                                <Button
                                    variant='outline'
                                    size='small'
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleSectionToggle('visaDetails')
                                    }}
                                >
                                    {sections.visaDetails.expanded ? '−' : '+'}
                                </Button>
                            </Container>

                            {sections.visaDetails.expanded && (
                                <>
                                    <VisaDetailsStep
                                        deviceType={DESKTOP}
                                        onContinue={handleVisaDetailsUpdate}
                                        isButtonLoading={
                                            sections.visaDetails.loading
                                        }
                                    />
                                    {sections.visaDetails.error && (
                                        <Typography
                                            variant='B3'
                                            className='mt-3 text-red-500'
                                        >
                                            {sections.visaDetails.error}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Card>

                        {/* Traveller Information Section */}
                        <Card
                            className={`mb-4 p-4 ${sections.traveller.expanded ? 'border-primary' : 'border-default'} ${!sections.visaDetails.completed ? 'opacity-50' : ''}`}
                        >
                            <Container
                                className='flex flex-between items-center mb-4'
                                onClick={() =>
                                    sections.visaDetails.completed &&
                                    !sections.traveller.loading &&
                                    handleSectionToggle('traveller')
                                }
                                style={{
                                    cursor: sections.visaDetails.completed
                                        ? 'pointer'
                                        : 'not-allowed'
                                }}
                            >
                                <Container
                                    className='flex items-center'
                                    style={{
                                        alignItems: 'center'
                                    }}
                                >
                                    <Typography variant='HM2'>
                                        Traveller Information
                                    </Typography>
                                    {sections.traveller.completed && (
                                        <div
                                            style={{
                                                marginLeft: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                lineHeight: 1
                                            }}
                                        >
                                            <ValidTick
                                                color='#00B28A'
                                                width='16'
                                                height='16'
                                            />
                                        </div>
                                    )}
                                    {!sections.visaDetails.completed && (
                                        <Container
                                            style={{ paddingLeft: '8px' }}
                                        >
                                            <Typography
                                                variant='B3'
                                                color='secondary'
                                            >
                                                Complete Visa Details first
                                            </Typography>
                                        </Container>
                                    )}
                                </Container>
                                {sections.visaDetails.completed && (
                                    <Button
                                        variant='outline'
                                        size='small'
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleSectionToggle('traveller')
                                        }}
                                    >
                                        {sections.traveller.expanded
                                            ? '−'
                                            : '+'}
                                    </Button>
                                )}
                            </Container>

                            {sections.traveller.expanded &&
                                sections.visaDetails.completed && (
                                    <>
                                        <TravellerInfoStep
                                            deviceType={DESKTOP}
                                        />
                                        {sections.traveller.error && (
                                            <Typography
                                                variant='B3'
                                                className='mt-3 text-red-500'
                                            >
                                                {sections.traveller.error}
                                            </Typography>
                                        )}
                                        <Container className='flex flex-between items-center mt-4'>
                                            <Container
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0
                                                }}
                                            >
                                                <Checkbox
                                                    checked={true}
                                                    disabled={true}
                                                    selectAll={false}
                                                    id='terms-checkbox-desktop-new'
                                                    label={''}
                                                    onChange={() => {}}
                                                    onClick={() => {}}
                                                    style={{
                                                        pointerEvents: 'none',
                                                        marginRight: 2
                                                    }}
                                                    children={<></>}
                                                />
                                                <Typography variant='B2'>
                                                    <span
                                                        style={{
                                                            color: '#888',
                                                            marginRight: '4px'
                                                        }}
                                                    >
                                                        I agree to the
                                                    </span>
                                                </Typography>
                                                <Typography color='link' variant='B2'>
                                                    <span
                                                        onClick={() => setIsModalOpen(true)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        Terms & Conditions
                                                    </span>
                                                </Typography>
                                            </Container>
                                            <div style={{ flex: 1 }} />
                                            <Button
                                                color='secondary'
                                                size='large'
                                                onClick={
                                                    handleTravellerInfoUpdate
                                                }
                                                isLoading={
                                                    sections.traveller.loading
                                                }
                                                disabled={
                                                    sections.traveller.loading
                                                }
                                            >
                                                Proceed to Review
                                            </Button>
                                        </Container>
                                    </>
                                )}
                        </Card>
                    </Container>

                    <Container className='flex flex-between items-center mt-6'>
                        <Image
                            src={MusafirLogo}
                            alt='Musafir logo'
                            height={20}
                            style={{
                                marginRight: 0,
                                marginLeft: 0,
                                alignSelf: 'end',
                                width: 'auto'
                            }}
                            priority
                        />
                    </Container>

                    <Spacer className='mb-8' />
                    <Divider height={1} color='#DDE1E6' />
                    {signedIn ? (
                        <VisaBookingsList deviceType={DESKTOP} />
                    ) : (
                        <RetriveApplicationCallout
                            onLoginClick={handleLoginClick}
                        />
                    )}
                </Card>
                <PageTracker
                    pageName='new_visa_flow_desktop'
                    attributes={
                        searchParams.get('utm_source')
                            ? { utm_source: searchParams.get('utm_source') }
                            : undefined
                    }
                />
            </Container>
            {!isServiceRuleLoading && serviceRule !== undefined && (
                <TermsAndConditionsModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    serviceRule={serviceRule}
                    deviceType={'DESKTOP'}
                />
            )}
        </>
    )
}

export default NewVisaUnifiedFlow
